// Déposer une annonce - Formulaire de création
// Utilise les mêmes rayons que la Marketplace pour une recherche unifiée
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, Upload, X, Camera, Euro, MapPin, Tag, FileText, Check,
  ChevronRight, ChevronLeft, AlertCircle, Image, Truck, Info, Search, ArrowLeft
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types d'annonces - Particuliers peuvent: vendre, louer, échanger, donner
const TYPES_ANNONCE = [
  { id: 'vente', name: 'Vente', description: 'Je vends cet article', icon: '💰' },
  { id: 'location', name: 'Location', description: 'Je loue ce matériel', icon: '📅' },
  { id: 'echange', name: 'Échange', description: 'J\'échange contre autre chose', icon: '🔄' },
  { id: 'don', name: 'Don', description: 'Je donne gratuitement', icon: '🎁' },
];

// États du produit
const ETATS = [
  { id: 'neuf', name: 'Neuf', description: 'Jamais utilisé, dans son emballage' },
  { id: 'tres_bon', name: 'Très bon état', description: 'Utilisé avec soin, comme neuf' },
  { id: 'bon', name: 'Bon état', description: 'Quelques traces d\'utilisation' },
  { id: 'usage', name: 'État correct', description: 'Usé mais fonctionnel' },
];

export default function DeposerAnnoncePage({ onSelectTool }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Rayons chargés depuis l'API (mêmes que Marketplace)
  const [rayons, setRayons] = useState([]);
  const [selectedRayon, setSelectedRayon] = useState(null);
  const [selectedSousRayon, setSelectedSousRayon] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showRayonPicker, setShowRayonPicker] = useState(false);
  const [rayonStep, setRayonStep] = useState('rayon'); // 'rayon' | 'sous_rayon' | 'category'
  
  const [formData, setFormData] = useState({
    type: '',
    rayon_id: '',
    rayon_name: '',
    sous_rayon_id: '',
    sous_rayon_name: '',
    category_id: '',
    category_name: '',
    titre: '',
    description: '',
    prix: '',
    etat: '',
    photos: [],
    ville: '',
    codePostal: '',
    livraison: false,
    negociable: false,
  });

  const [errors, setErrors] = useState({});

  // Charger les rayons au montage
  useEffect(() => {
    fetchRayons();
  }, []);

  const fetchRayons = async () => {
    try {
      const response = await fetch(`${API_URL}/api/marketplace/rayons/full`);
      if (response.ok) {
        const data = await response.json();
        setRayons(data);
      }
    } catch (error) {
      console.error('Error fetching rayons:', error);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Sélection d'un rayon
  const handleSelectRayon = (rayon) => {
    setSelectedRayon(rayon);
    setSelectedSousRayon(null);
    setSelectedCategory(null);
    setRayonStep('sous_rayon');
  };

  // Sélection d'un sous-rayon
  const handleSelectSousRayon = (sousRayon) => {
    setSelectedSousRayon(sousRayon);
    setSelectedCategory(null);
    setRayonStep('category');
  };

  // Sélection d'une catégorie (final)
  const handleSelectCategory = (category) => {
    setSelectedCategory(category);
    setFormData(prev => ({
      ...prev,
      rayon_id: selectedRayon.id,
      rayon_name: selectedRayon.name,
      sous_rayon_id: selectedSousRayon.id,
      sous_rayon_name: selectedSousRayon.name,
      category_id: category.id,
      category_name: category.name,
    }));
    setShowRayonPicker(false);
    setRayonStep('rayon');
  };

  // Retour dans le picker
  const handleRayonBack = () => {
    if (rayonStep === 'category') {
      setRayonStep('sous_rayon');
      setSelectedCategory(null);
    } else if (rayonStep === 'sous_rayon') {
      setRayonStep('rayon');
      setSelectedSousRayon(null);
    } else {
      setShowRayonPicker(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (formData.photos.length + files.length > 5) {
      toast.error('Maximum 5 photos autorisées');
      return;
    }
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          photos: [...prev.photos, { id: Date.now() + Math.random(), url: e.target.result, file }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (photoId) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter(p => p.id !== photoId)
    }));
  };

  const validateStep = (stepNum) => {
    const newErrors = {};
    
    if (stepNum === 1) {
      if (!formData.type) newErrors.type = 'Choisis un type d\'annonce';
      if (!formData.rayon_id) newErrors.rayon = 'Choisis un rayon';
    }
    
    if (stepNum === 2) {
      if (!formData.titre || formData.titre.length < 10) {
        newErrors.titre = 'Le titre doit contenir au moins 10 caractères';
      }
      if (!formData.description || formData.description.length < 20) {
        newErrors.description = 'La description doit contenir au moins 20 caractères';
      }
      if (formData.type !== 'don' && (!formData.prix || formData.prix <= 0)) {
        newErrors.prix = 'Indique un prix valide';
      }
      if (!formData.etat) newErrors.etat = 'Indique l\'état du produit';
    }
    
    if (stepNum === 3) {
      if (formData.photos.length === 0) {
        newErrors.photos = 'Ajoute au moins une photo';
      }
    }
    
    if (stepNum === 4) {
      if (!formData.ville) newErrors.ville = 'Indique ta ville';
      if (!formData.codePostal || !/^\d{5}$/.test(formData.codePostal)) {
        newErrors.codePostal = 'Code postal invalide';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;
    
    setLoading(true);
    
    try {
      // Vérifier si l'utilisateur est connecté
      const token = localStorage.getItem('client_token') || localStorage.getItem('buyer_token');
      
      if (!token) {
        toast.error('Vous devez être connecté et certifié pour déposer une annonce');
        navigate('/connexion?redirect=/cockpit-particulier/deposer');
        return;
      }
      
      // Préparer les données pour l'API ecosystem/listings
      const listingData = {
        title: formData.titre,
        description: formData.description,
        category: formData.rayon_id || 'materiaux', // Catégorie principale
        subcategory: formData.sous_rayon_id || null,
        price: formData.type === 'don' ? 0 : parseFloat(formData.prix) || 0,
        price_negotiable: formData.negociable,
        condition: formData.etat,
        photos: formData.photos.map(p => p.url),
        city: formData.ville,
        postal_code: formData.codePostal,
        delivery_available: formData.livraison,
        // Champs additionnels pour le BTP
        transaction_type: formData.type, // vente, location, echange, don
        rayon_id: formData.rayon_id,
        rayon_name: formData.rayon_name,
        sous_rayon_id: formData.sous_rayon_id,
        sous_rayon_name: formData.sous_rayon_name,
        category_id: formData.category_id,
        category_name: formData.category_name,
      };
      
      // Appeler l'API pour créer l'annonce
      const response = await fetch(`${API_URL}/api/ecosystem/listings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(listingData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erreur lors de la création de l\'annonce');
      }
      
      const result = await response.json();
      console.log('Annonce créée:', result);
      
      toast.success('Annonce publiée avec succès !');
      navigate('/cockpit-particulier/annonces');
    } catch (error) {
      console.error('Erreur publication:', error);
      toast.error(error.message || 'Erreur lors de la publication');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 pb-24">
      {/* Header - Compact sur mobile */}
      <div className="mb-4 flex items-center gap-3">
        {onSelectTool && (
          <button 
            onClick={() => onSelectTool('hub_marketplace', 'Marketplace')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Retour au tableau de bord"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
        )}
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Déposer une annonce</h1>
          <p className="text-xs text-gray-500">Vends, donne, loue ou échange en quelques clics</p>
        </div>
      </div>

      {/* Progress - Optimisé mobile */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-1.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`
                w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm
                ${step >= s 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-gray-200 text-gray-500'
                }
              `}>
                {step > s ? <Check size={14} /> : s}
              </div>
              {s < 5 && (
                <div className={`w-8 sm:w-20 h-0.5 mx-0.5 sm:mx-1 ${step > s ? 'bg-orange-500' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[10px] sm:text-xs text-gray-500 px-1">
          <span>Type</span>
          <span>Détails</span>
          <span>Photos</span>
          <span>Lieu</span>
          <span>Publier</span>
        </div>
      </div>

      {/* Step Content */}
      <motion.div
        key={step}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6"
      >
        {/* Step 1: Type & Catégorie */}
        {step === 1 && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">Type d'annonce</label>
              <div className="grid grid-cols-2 gap-2.5">
                {TYPES_ANNONCE.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => updateField('type', type.id)}
                    className={`p-3 sm:p-4 rounded-xl border-2 text-left transition-all ${
                      formData.type === type.id
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-medium text-gray-900 text-sm">{type.name}</p>
                    <p className="text-[11px] text-gray-500 leading-tight">{type.description}</p>
                  </button>
                ))}
              </div>
              {errors.type && <p className="text-red-500 text-sm mt-2">{errors.type}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">Rayon (même classification que la Marketplace)</label>
              
              {/* Bouton pour ouvrir le picker */}
              <button
                onClick={() => setShowRayonPicker(true)}
                className={`w-full p-3 sm:p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${
                  formData.rayon_id
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {formData.rayon_id ? (
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{formData.rayon_name}</p>
                    <p className="text-xs text-gray-500">
                      {formData.sous_rayon_name} → {formData.category_name}
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Tag size={18} />
                    <span className="text-sm">Choisir un rayon...</span>
                  </div>
                )}
                <ChevronRight size={18} className="text-gray-400" />
              </button>
              {errors.rayon && <p className="text-red-500 text-sm mt-2">{errors.rayon}</p>}
            </div>
          </div>
        )}

        {/* Modal Picker Rayon */}
        <AnimatePresence>
          {showRayonPicker && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 md:left-[288px] z-[10002] bg-black/50 flex items-end sm:items-center justify-center"
              onClick={() => setShowRayonPicker(false)}
            >
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                onClick={(e) => e.stopPropagation()}
                className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-2xl max-h-[80vh] overflow-hidden"
              >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gradient-to-r from-orange-500 to-orange-600">
                  <button onClick={handleRayonBack} className="p-2 hover:bg-white/10 rounded-full">
                    <ChevronLeft size={24} className="text-white" />
                  </button>
                  <h3 className="font-semibold text-white">
                    {rayonStep === 'rayon' && 'Choisir un rayon'}
                    {rayonStep === 'sous_rayon' && selectedRayon?.name}
                    {rayonStep === 'category' && selectedSousRayon?.name}
                  </h3>
                  <button onClick={() => setShowRayonPicker(false)} className="p-2 hover:bg-white/10 rounded-full">
                    <X size={24} className="text-white" />
                  </button>
                </div>

                {/* Content */}
                <div className="overflow-y-auto max-h-[60vh] p-4">
                  {/* Liste des rayons */}
                  {rayonStep === 'rayon' && (
                    <div className="space-y-2">
                      {rayons.map((rayon) => (
                        <button
                          key={rayon.id}
                          onClick={() => handleSelectRayon(rayon)}
                          className="w-full flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-all"
                        >
                          <span className="text-2xl">{rayon.icon}</span>
                          <span className="font-medium text-gray-900 flex-1 text-left">{rayon.name}</span>
                          <ChevronRight size={20} className="text-gray-400" />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Liste des sous-rayons */}
                  {rayonStep === 'sous_rayon' && selectedRayon && (
                    <div className="space-y-2">
                      {selectedRayon.sous_rayons?.map((sr) => (
                        <button
                          key={sr.id}
                          onClick={() => handleSelectSousRayon(sr)}
                          className="w-full flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-all"
                        >
                          <span className="font-medium text-gray-900 flex-1 text-left">{sr.name}</span>
                          <ChevronRight size={20} className="text-gray-400" />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Liste des catégories */}
                  {rayonStep === 'category' && selectedSousRayon && (
                    <div className="space-y-2">
                      {selectedSousRayon.categories?.map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => handleSelectCategory(cat)}
                          className="w-full flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-all"
                        >
                          <span className="font-medium text-gray-900 flex-1 text-left">{cat.name}</span>
                          <Check size={20} className="text-orange-500 opacity-0 group-hover:opacity-100" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step 2: Détails */}
        {step === 2 && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Titre de l'annonce *
              </label>
              <input
                type="text"
                value={formData.titre}
                onChange={(e) => updateField('titre', e.target.value)}
                placeholder="Ex: Perceuse Bosch Professional 18V - Excellent état"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                maxLength={100}
              />
              <p className="text-xs text-gray-400 mt-1">{formData.titre.length}/100 caractères</p>
              {errors.titre && <p className="text-red-500 text-sm mt-1">{errors.titre}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Décris ton article en détail : marque, dimensions, état, raison de la vente..."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 min-h-[120px]"
                maxLength={2000}
              />
              <p className="text-xs text-gray-400 mt-1">{formData.description.length}/2000 caractères</p>
              {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
            </div>

            {formData.type !== 'don' && (
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Prix *
                </label>
                <div className="relative">
                  <Euro size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="number"
                    value={formData.prix}
                    onChange={(e) => updateField('prix', e.target.value)}
                    placeholder="0"
                    className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <label className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    checked={formData.negociable}
                    onChange={(e) => updateField('negociable', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-600">Prix négociable</span>
                </label>
                {errors.prix && <p className="text-red-500 text-sm mt-1">{errors.prix}</p>}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-3">État *</label>
              <div className="space-y-2">
                {ETATS.map((etat) => (
                  <button
                    key={etat.id}
                    onClick={() => updateField('etat', etat.id)}
                    className={`w-full p-3 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                      formData.etat === etat.id
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      formData.etat === etat.id ? 'border-orange-500 bg-orange-500' : 'border-gray-300'
                    }`}>
                      {formData.etat === etat.id && <Check size={12} className="text-white" />}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{etat.name}</p>
                      <p className="text-xs text-gray-500">{etat.description}</p>
                    </div>
                  </button>
                ))}
              </div>
              {errors.etat && <p className="text-red-500 text-sm mt-2">{errors.etat}</p>}
            </div>
          </div>
        )}

        {/* Step 3: Photos */}
        {step === 3 && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Photos * <span className="text-gray-400 font-normal">(max 5)</span>
              </label>
              
              <div className="grid grid-cols-3 gap-3 mb-4">
                {formData.photos.map((photo, index) => (
                  <div key={photo.id} className="relative aspect-square rounded-xl overflow-hidden bg-gray-100">
                    <img src={photo.url} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => removePhoto(photo.id)}
                      className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    >
                      <X size={14} />
                    </button>
                    {index === 0 && (
                      <span className="absolute bottom-2 left-2 text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">
                        Photo principale
                      </span>
                    )}
                  </div>
                ))}
                
                {formData.photos.length < 5 && (
                  <label className="aspect-square rounded-xl border-2 border-dashed border-gray-300 hover:border-orange-500 cursor-pointer flex flex-col items-center justify-center gap-2 transition-colors">
                    <Camera size={24} className="text-gray-400" />
                    <span className="text-xs text-gray-500">Ajouter</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {errors.photos && <p className="text-red-500 text-sm">{errors.photos}</p>}
              
              <div className="bg-blue-50 rounded-xl p-4 flex items-start gap-3">
                <Info size={20} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Conseils pour de bonnes photos</p>
                  <ul className="list-disc pl-4 text-xs space-y-1 text-blue-700">
                    <li>Prends des photos nettes et bien éclairées</li>
                    <li>Montre l'article sous plusieurs angles</li>
                    <li>Photographie les éventuels défauts</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Localisation */}
        {step === 4 && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Ville *
              </label>
              <div className="relative">
                <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={formData.ville}
                  onChange={(e) => updateField('ville', e.target.value)}
                  placeholder="Ex: Paris"
                  className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              {errors.ville && <p className="text-red-500 text-sm mt-1">{errors.ville}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Code postal *
              </label>
              <input
                type="text"
                value={formData.codePostal}
                onChange={(e) => updateField('codePostal', e.target.value)}
                placeholder="Ex: 75001"
                maxLength={5}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
              {errors.codePostal && <p className="text-red-500 text-sm mt-1">{errors.codePostal}</p>}
            </div>

            <div>
              <label className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-orange-500 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={formData.livraison}
                  onChange={(e) => updateField('livraison', e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Truck size={18} className="text-gray-500" />
                    <span className="font-medium text-gray-900">Je propose la livraison</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">L'acheteur pourra demander l'envoi</p>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* Step 5: Récapitulatif */}
        {step === 5 && (
          <div className="space-y-5">
            <h2 className="text-lg font-bold text-gray-900">Récapitulatif</h2>
            
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              {formData.photos[0] && (
                <img 
                  src={formData.photos[0].url} 
                  alt="" 
                  className="w-full h-48 object-cover rounded-lg"
                />
              )}
              
              <div>
                <span className="text-xs text-orange-600 font-medium uppercase">
                  {TYPES_ANNONCE.find(t => t.id === formData.type)?.name} • {CATEGORIES.find(c => c.id === formData.categorie)?.name}
                </span>
                <h3 className="text-lg font-bold text-gray-900 mt-1">{formData.titre}</h3>
                <p className="text-2xl font-bold text-orange-600 mt-2">
                  {formData.type === 'don' ? 'Gratuit' : `${formData.prix} €`}
                  {formData.negociable && <span className="text-sm font-normal text-gray-500 ml-2">(Négociable)</span>}
                </p>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600">
                <MapPin size={14} />
                <span>{formData.ville} ({formData.codePostal})</span>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Tag size={14} />
                <span>{ETATS.find(e => e.id === formData.etat)?.name}</span>
              </div>

              {formData.livraison && (
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <Truck size={14} />
                  <span>Livraison possible</span>
                </div>
              )}
            </div>

            <div className="bg-green-50 rounded-xl p-4 flex items-start gap-3">
              <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-green-800">
                <p className="font-medium">Ton annonce est prête !</p>
                <p className="text-xs text-green-600 mt-1">
                  Elle sera visible par tous les utilisateurs après publication.
                </p>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Navigation Buttons - Sticky sur mobile */}
      <div className="flex items-center justify-between mt-4 pt-4 sticky bottom-0 bg-white pb-4 border-t border-gray-100 -mx-4 px-4 sm:static sm:border-0 sm:mx-0 sm:px-0 sm:pb-0">
        {step > 1 ? (
          <Button
            variant="outline"
            onClick={prevStep}
            className="flex items-center gap-1.5 text-sm h-10"
          >
            <ChevronLeft size={16} />
            Retour
          </Button>
        ) : (
          <div />
        )}

        {step < 5 ? (
          <Button
            onClick={nextStep}
            className="bg-orange-500 hover:bg-orange-600 text-white flex items-center gap-1.5 text-sm h-10 px-5"
          >
            Continuer
            <ChevronRight size={16} />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-6 h-10 text-sm"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                Publication...
              </>
            ) : (
              <>
                <Check size={16} className="mr-1.5" />
                Publier l'annonce
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
